import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import Breadcrumbs from "../components/Breadcrumbs";
import AdminNav from "../components/AdminNav";
import RequireAdmin from "../components/RequireAdmin";
import {
  confirmMapping,
  listMappings,
  markNoMatch,
  priceStatus,
  searchTcgplayerGroups,
  suggestMappingsAll,
  triggerPriceRefresh,
  triggerPriceRefreshSet,
} from "../lib/api";

const STATUS_ORDER = ["suggested", "unmapped", "confirmed", "no_match"];

export default function AdminMappingsPage() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("suggested");
  const [state, setState] = useState("loading");
  const [pStat, setPStat] = useState(null);

  const load = useCallback(async () => {
    setState("loading");
    try {
      const [d, ps] = await Promise.all([
        listMappings({ status }),
        priceStatus(),
      ]);
      setData(d);
      setPStat(ps);
      setState("ready");
    } catch (e) { setState("error"); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  async function runSuggest() {
    await suggestMappingsAll();
    toast("Suggestion pass started.");
    setTimeout(load, 1200);
  }
  async function runRefresh() {
    await triggerPriceRefresh();
    toast("Full price refresh started.");
  }

  return (
    <RequireAdmin>
      <div
        data-testid="admin-mappings"
        className="mx-auto w-full max-w-[1200px] px-5 py-8 md:px-gutter md:py-10"
      >
        <Breadcrumbs items={[
          { label: "Home", to: "/" },
          { label: "Admin", to: "/admin" },
          { label: "Price mappings" },
        ]} />
        <h1 className="mt-6 text-title-page text-ink-text">Price mappings</h1>
        <AdminNav />

        {/* Global controls */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <button
            data-testid="mappings-run-suggest"
            onClick={runSuggest}
            className="rounded-card border border-ink-chip bg-ink-surface px-4 py-2 text-body text-ink-text hover:border-ink-muted"
          >
            Re-run suggestion pass
          </button>
          <button
            data-testid="mappings-refresh-prices"
            onClick={runRefresh}
            className="rounded-card bg-[#4353E8] px-4 py-2 text-body font-semibold text-white hover:translate-y-[-1px]"
          >
            Refresh prices now
          </button>
          {pStat?.sync?.progress && (
            <span className="text-meta text-ink-muted tabular">
              last refresh: {pStat.sync.status} · {pStat.sync.progress.priced || 0} priced
            </span>
          )}
        </div>

        {/* Status filter chips */}
        <div className="mb-4 flex flex-wrap gap-2">
          {STATUS_ORDER.map((s) => (
            <button
              key={s}
              data-testid={`mapping-filter-${s}`}
              onClick={() => setStatus(s)}
              className={
                "rounded-chip border px-3 py-1.5 text-meta " +
                (status === s
                  ? "border-indigo-text bg-indigo-chip-bg text-indigo-chip-text"
                  : "border-ink-chip bg-ink-surface text-ink-muted hover:text-ink-text")
              }
            >
              {s.replace("_", " ")} · {data?.totals?.[s] ?? "…"}
            </button>
          ))}
        </div>

        {state === "loading" && <div className="text-body text-ink-muted">Loading…</div>}
        {state === "error" && <div className="text-body text-ink-muted">Failed to load.</div>}
        {state === "ready" && data && (
          <ul className="space-y-3" data-testid="mappings-list">
            {data.sets.length === 0 && (
              <li className="rounded-card border border-ink-chip bg-ink-surface p-6 text-body text-ink-muted">
                Nothing in this bucket.
              </li>
            )}
            {data.sets.map((s) => (
              <MappingRow key={s.id} row={s} onChanged={load} />
            ))}
          </ul>
        )}
      </div>
    </RequireAdmin>
  );
}

function MappingRow({ row, onChanged }) {
  const [showPicker, setShowPicker] = useState(false);
  const [picker, setPicker] = useState({ q: "", results: [] });

  async function pickerSearch(q) {
    setPicker({ q, results: [] });
    if (!q || q.length < 2) return;
    try {
      const results = await searchTcgplayerGroups(q);
      setPicker({ q, results });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Group search failed");
    }
  }

  async function doConfirm(groupId, groupName) {
    try {
      await confirmMapping(row.external_id, groupId, groupName);
      toast.success(`${row.name} → ${groupName || groupId}`);
      setShowPicker(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Confirm failed");
    }
  }

  async function doNoMatch() {
    if (!window.confirm(`Mark "${row.name}" as no-match?`)) return;
    await markNoMatch(row.external_id);
    onChanged();
  }

  async function refreshOne() {
    try {
      const r = await triggerPriceRefreshSet(row.external_id);
      toast.success(`${r.priced} cards priced`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    }
  }

  return (
    <li
      data-testid={`mapping-row-${row.external_id}`}
      className="rounded-card border border-ink-chip bg-ink-surface p-4"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="text-title-section text-ink-text">{row.name}</div>
          <div className="mt-1 text-meta text-ink-muted tabular">
            {row.code || "—"} · released {row.release_date || "?"}
          </div>
        </div>
        <StatusChip status={row.mapping_status} confidence={row.mapping_confidence} />
      </div>

      {row.tcgplayer_group_id && (
        <div className="mt-2 text-meta text-ink-muted">
          mapped to <span className="text-ink-text">{row.tcgplayer_group_name || row.tcgplayer_group_id}</span>
          {" "}(group #{row.tcgplayer_group_id})
        </div>
      )}
      {row.prices_last_synced_at && (
        <div className="mt-1 text-meta text-ink-muted tabular">
          prices synced {row.prices_last_synced_at.slice(0, 10)} · {row.prices_last_status || "ok"}
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {row.mapping_status === "suggested" && row.tcgplayer_group_id && (
          <button
            data-testid={`mapping-confirm-${row.external_id}`}
            onClick={() => doConfirm(row.tcgplayer_group_id, row.tcgplayer_group_name)}
            className="rounded-card bg-[#4353E8] px-4 py-2 text-body font-semibold text-white hover:translate-y-[-1px]"
          >
            Confirm suggestion
          </button>
        )}
        <button
          data-testid={`mapping-change-${row.external_id}`}
          onClick={() => setShowPicker((s) => !s)}
          className="rounded-card border border-ink-chip bg-ink-surface px-4 py-2 text-body text-ink-text hover:border-ink-muted"
        >
          {showPicker ? "Cancel" : "Change / search"}
        </button>
        <button
          data-testid={`mapping-no-match-${row.external_id}`}
          onClick={doNoMatch}
          className="rounded-card border border-ink-chip bg-ink-surface px-4 py-2 text-meta text-ink-muted hover:text-ink-text"
        >
          No match
        </button>
        {row.mapping_status === "confirmed" && (
          <button
            data-testid={`mapping-refresh-${row.external_id}`}
            onClick={refreshOne}
            className="rounded-card border border-ink-chip bg-ink-surface px-4 py-2 text-meta text-ink-muted hover:text-ink-text"
          >
            Refresh prices
          </button>
        )}
      </div>

      {showPicker && (
        <div className="mt-3 rounded-card border border-ink-chip bg-ink-bg p-3">
          <input
            data-testid={`mapping-picker-input-${row.external_id}`}
            type="text"
            autoFocus
            placeholder="Search TCGplayer groups…"
            value={picker.q}
            onChange={(e) => pickerSearch(e.target.value)}
            className="w-full rounded-chip border border-ink-chip bg-ink-surface px-3 py-2 text-body text-ink-text focus:outline-none focus:ring-2 focus:ring-indigo-text"
          />
          <ul className="mt-2 max-h-64 overflow-y-auto">
            {picker.results.map((g) => (
              <li key={g.groupId}>
                <button
                  data-testid={`mapping-picker-pick-${g.groupId}`}
                  onClick={() => doConfirm(g.groupId, g.name)}
                  className="w-full rounded-chip px-3 py-2 text-left text-body text-ink-text hover:bg-ink-raised"
                >
                  {g.name}
                  <span className="ml-2 text-meta text-ink-muted tabular">
                    {g.abbreviation || ""} · {g.publishedOn?.slice(0, 10) || ""}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  );
}

function StatusChip({ status, confidence }) {
  const map = {
    unmapped: "border-ink-chip bg-ink-bg text-ink-muted",
    suggested: "border-indigo-chip-border bg-indigo-chip-bg text-indigo-chip-text",
    confirmed: "border-[#254a2b] bg-[#122019] text-[#8FE3A1]",
    no_match: "border-ink-chip bg-ink-bg text-ink-muted",
  };
  return (
    <span
      data-testid={`mapping-chip-${status}`}
      className={`inline-flex items-center rounded-chip border px-2 py-0.5 text-eyebrow uppercase ${map[status] || map.unmapped}`}
    >
      {status.replace("_", " ")}
      {confidence != null && ` · ${Math.round(confidence * 100)}%`}
    </span>
  );
}
